import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, children, disabled, className = '', ...props }, ref) => {
    const base =
      'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-150 cursor-pointer border disabled:opacity-50 disabled:cursor-not-allowed'

    const variants = {
      primary:
        'bg-[#0a4ee3] text-white border-[#0a4ee3] hover:bg-[#0842c4] hover:border-[#0842c4] shadow-sm',
      secondary:
        'bg-white text-[#0f172a] border-[#e2e8f0] hover:bg-[#f8fafc] hover:border-[#cbd5e1]',
      outline: 'bg-transparent text-[#0a4ee3] border-[#0a4ee3] hover:bg-[#e8f0fd]',
      ghost:
        'bg-transparent text-[#64748b] border-transparent hover:bg-[#f1f5f9] hover:text-[#0f172a]',
      danger: 'bg-[#ef4444] text-white border-[#ef4444] hover:bg-[#dc2626] shadow-sm',
    }

    const sizes = {
      sm: 'px-3 py-1.5 text-xs',
      md: 'px-4 py-2 text-sm',
      lg: 'px-5 py-2.5 text-sm',
    }

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
        {...props}
      >
        {loading && (
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {children}
      </button>
    )
  },
)

Button.displayName = 'Button'
